import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { useAuth } from '../context/AuthContext';
import MessageBox from '../components/MessageBox';
import { 
    BANKING_DETAILS, 
    ADMIN_WHATSAPP_WEBHOOK_URL_1, 
    ADMIN_WHATSAPP_WEBHOOK_URL_2 
} from '../utils/constants';

function PackagesPage({ userDetails }) {
    const { currentUser } = useAuth();
    const [allPackages, setAllPackages] = useState([]);
    const [userRequests, setUserRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [packageToConfirm, setPackageToConfirm] = useState(null); 

    // Fetch active packages and the current user's request history
    useEffect(() => {
        if (!db || !currentUser) return;

        // 1. Fetch Active Packages
        const packagesColRef = collection(db, 'artifacts', appId, 'public', 'data', 'packages');
        const qPackages = query(packagesColRef, where('status', '==', 'active'));
        const unsubscribePackages = onSnapshot(qPackages, (snapshot) => {
            const fetchedPackages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllPackages(fetchedPackages);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching packages:", error);
            setMessage("Could not load packages. Please try again later.");
            setMessageType("error");
            setLoading(false);
        });

        // 2. Fetch User's Requests
        const requestsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'userPackages');
        const qRequests = query(requestsColRef, where('userId', '==', currentUser.uid));
        const unsubscribeRequests = onSnapshot(qRequests, (snapshot) => {
            const fetchedRequests = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(), 
                requestedAt: doc.data().requestedAt?.toDate() 
            }));
            setUserRequests(fetchedRequests);
        }, (error) => {
            console.error("Error fetching user requests:", error);
        });

        return () => {
            unsubscribePackages();
            unsubscribeRequests();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]); 

    const handleRequestPackage = async (pkg) => {
        if (!userDetails || !db) return;
        
        // Prevent multiple pending requests
        const hasPendingRequest = userRequests.some(req => req.status === 'pending');
        if (hasPendingRequest) {
            setMessage("You already have a pending package request. Please wait for the admin to approve it before submitting another one.");
            setMessageType("error");
            return;
        }
        
        setMessage('');
        setLoading(true);
        try {
            const requestsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'userPackages');
            await addDoc(requestsColRef, {
                userId: userDetails.uid,
                userName: userDetails.displayName || userDetails.email,
                userColor: userDetails.color || '#60a5fa',
                packageId: pkg.id,
                packageName: pkg.name,
                slotsAdded: pkg.slots,
                status: 'pending',
                requestedAt: new Date(),
            });

            // Send WhatsApp Notification to Admin
            if (ADMIN_WHATSAPP_WEBHOOK_URL_1 || ADMIN_WHATSAPP_WEBHOOK_URL_2) {
                try {
                    const whatsappMessage = `📦 *New Package Order! 📦* \nUser: ${userDetails.displayName || userDetails.email}\nPackage: ${pkg.name}\nSpots: ${pkg.slots}\nCost: R ${pkg.cost?.toFixed(2) || '0.00'}`;
                    
                    const sendNotification = async (webhookUrl) => {
                        if (!webhookUrl) return;
                        const separator = webhookUrl.includes('?') ? '&' : '?';
                        const fetchUrl = `${webhookUrl}${separator}text=${encodeURIComponent(whatsappMessage)}`;
                        await fetch(fetchUrl, { mode: 'no-cors' });
                    };

                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_1);
                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_2); 

                    console.log("WhatsApp package notification signals sent.");
                } catch (notifyErr) {
                    console.warn("Failed to trigger WhatsApp notification:", notifyErr);
                }
            }

            // Show success modal
            setPackageToConfirm(pkg); 
            
        } catch (error) {
            console.error("Error requesting package:", error);
            setMessage(`Error requesting package: ${error.message}`);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };
    
    const handleClosePackageRequestModal = () => {
        setPackageToConfirm(null);
    };

    const getRequestStatus = (packageId) => {
        // Find the most recent request for this specific package
        const packageRequests = userRequests
            .filter(req => req.packageId === packageId)
            .sort((a, b) => b.requestedAt.getTime() - a.requestedAt.getTime()); 
            
        return packageRequests.length > 0 ? packageRequests[0].status : null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-6">
                <div className="text-xl font-semibold text-gray-700">Loading packages...</div>
            </div>
        );
    }
    
    const hasAnyPendingRequest = userRequests.some(req => req.status === 'pending');
    
    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />

            {packageToConfirm && (
                <MessageBox
                    message={`Request for "${packageToConfirm.name}" submitted! Waiting for approval.`}
                    onClose={handleClosePackageRequestModal}
                >
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <h4 className="text-xl font-bold text-gray-800 mb-3">
                            Step 1: Make Payment
                        </h4>
                        <p className="text-sm text-gray-700 mb-4">
                            Please complete your payment of <span className="font-extrabold text-green-700">R {packageToConfirm.cost?.toFixed(2) || '0.00'}</span> using the details below.
                            The package will be approved once payment reflects.
                        </p>
                        
                        <div className="flex flex-col md:flex-row items-center justify-center gap-4 bg-white p-4 rounded-lg shadow-inner">
                            <div className="text-left space-y-1 text-sm flex-1 min-w-0">
                                <p className="font-bold text-lg text-blue-700">Banking Details</p>
                                <p><span className="font-semibold">Bank:</span> {BANKING_DETAILS.bank}</p>
                                <p><span className="font-semibold">Account:</span> {BANKING_DETAILS.accountNumber}</p>
                                <p><span className="font-semibold">Ref:</span> <span className="font-mono text-red-500">{userDetails.email}</span></p>
                            </div>

                            <div className="flex flex-col items-center flex-1">
                                <img 
                                    src={BANKING_DETAILS.qrCodeUrl}
                                    alt="Scan to Pay QR Code" 
                                    className="w-32 h-32 object-contain rounded-lg border border-gray-300"
                                    onError={(e) => { 
                                        e.target.onerror = null; 
                                        e.target.parentElement.innerHTML = '<span class=\'text-center text-red-500 text-xs p-2\'>QR Code Error</span>'; 
                                    }}
                                />
                                <p className="text-xs font-bold text-gray-600 mt-1">Scan to Pay</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h4 className="text-xl font-bold text-gray-800 mb-3">Step 2: Await Approval</h4>
                        <p className="text-sm text-gray-700 mb-4">
                            The admin has been notified. You can close this window now. The package details will remain on the "Your Package Request History" table below until approved.
                        </p>
                        <button
                            onClick={handleClosePackageRequestModal}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Close Window
                        </button>
                    </div>
                </MessageBox>
            )}

            <h2 className="font-display text-4xl sm:text-5xl text-gray-800 mb-6 text-center tracking-wider">
                Available Packages
            </h2>
            
            {allPackages.length === 0 ? (
                <p className="text-center text-gray-600 text-lg">No active packages are available at this time.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {allPackages.map(pkg => {
                        const userStatus = getRequestStatus(pkg.id);
                        const isApproved = userStatus === 'approved';
                        const isRejected = userStatus === 'rejected';

                        let buttonText = "Request Package";
                        let buttonClass = "bg-green-600 hover:bg-green-700";
                        let disabled = false;

                        if (hasAnyPendingRequest) {
                            buttonText = "Pending Request Exists";
                            buttonClass = "bg-gray-400 cursor-not-allowed";
                            disabled = true;
                        } else if (isApproved) {
                            buttonText = "Re-purchase Package";
                            buttonClass = "bg-indigo-600 hover:bg-indigo-700";
                            disabled = false;
                        } else if (isRejected) {
                            buttonText = "Re-request Package";
                            buttonClass = "bg-orange-600 hover:bg-orange-700";
                            disabled = false;
                        }
                        
                        return (
                            <div key={pkg.id} className="bg-gray-50 p-6 rounded-lg shadow-md border-t-4 border-green-500">
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                                <p className="text-xl text-green-700 font-extrabold mb-3">R {pkg.cost?.toFixed(2) || '0.00'}</p>
                                <p className="text-lg text-green-700 font-semibold mb-3">{pkg.slots} Spots</p>
                                <p className="text-sm text-gray-600 mb-4">Must be used within {pkg.duration} day(s).</p>
                                <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                                <button
                                    onClick={() => handleRequestPackage(pkg)}
                                    disabled={disabled}
                                    className={`w-full py-3 px-4 text-white font-bold rounded-lg transition duration-200 ${buttonClass}`}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {userRequests.length > 0 && (
                <div className="mt-12 p-6 border border-gray-200 rounded-lg shadow-sm">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Your Package Request History</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow-md">
                            <thead className="bg-gray-100 border-b border-gray-200">
                                <tr>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Package</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Status</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Requested</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userRequests.map(req => (
                                    <tr key={req.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                        <td className="py-3 px-4 text-gray-800 text-sm">{req.packageName}</td>
                                        <td className="py-3 px-4 text-sm font-bold">
                                            <span className={`px-2 py-1 rounded-full text-xs ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : req.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                            </span>
                                            {req.status === 'rejected' && req.rejectionReason && (
                                                <p className="text-xs text-red-600 font-normal mt-1 italic">
                                                    Reason: {req.rejectionReason}
                                                </p>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-gray-800 text-sm">{req.requestedAt.toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PackagesPage;